// Anke, 2026-07-16 (Fehlerbericht "keine Schrift zu sehen") — Textboxen als
// ECHTEN SVG-Text ausgeben.
//
// Vorher reisten Textboxen als eingebettetes HTML (`<foreignObject>`). Browser
// stellen das perfekt dar, Zeichenprogramme wie Illustrator zeigen dort NICHTS.
// Damit der Text ueberall ankommt — sichtbar, auswaehlbar, bearbeitbar — setzen
// wir ihn hier selbst: Textlaeufe einsammeln, Zeilen umbrechen, `<text>` mit
// einem `<tspan>` je Lauf ausgeben.
//
// Der Preis, mit Anke abgestimmt: Den Umbruch rechnen WIR, nicht der Browser.
// Bei einfachem Text deckt sich das; bei Listen und Hoch-/Tiefstellung kann es
// leicht abweichen.

import type { TextBox } from '../../features/textbox/textbox.interface.js';
import type { TextDocument, TextDocumentNode } from '../../features/textbox/text-document.type.js';
import { EDIT_TEXTBOX_LINE_HEIGHT } from '../../features/textbox/textbox-html-builder.js';
import { num, esc, PORTABLE_FONT_FAMILY } from './svg-geometry.js';

/** Ein Stueck Text mit einheitlicher Auszeichnung. */
export interface TextRun {
  readonly text: string;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strike: boolean;
  readonly fontSize: number;
  readonly color: string;
}

/** Ein Absatz: Laeufe plus Ausrichtung und (bei Listen) ein Aufzaehlungszeichen. */
export interface TextBlock {
  readonly runs: readonly TextRun[];
  readonly align: 'left' | 'center' | 'right';
  readonly marker?: string;
}

/** Misst die Breite eines Textstuecks in der Auszeichnung des Laufs. */
export type Measure = (text: string, run: TextRun) => number;

const PX = /^([\d.]+)px$/;

function markAttrs(node: TextDocumentNode, base: { fontSize: number; color: string }): Omit<TextRun, 'text'> {
  let bold = false, italic = false, underline = false, strike = false;
  let fontSize = base.fontSize;
  let color = base.color;

  for (const mark of node.marks ?? []) {
    if (mark.type === 'bold') bold = true;
    else if (mark.type === 'italic') italic = true;
    else if (mark.type === 'underline') underline = true;
    else if (mark.type === 'strike') strike = true;
    else if (mark.type === 'textStyle') {
      const fs = mark.attrs?.['fontSize'];
      if (typeof fs === 'string') {
        const m = PX.exec(fs.trim());
        // Nur absolute px uebernehmen — `em` (Hoch-/Tiefstellung) ist relativ
        // und wuerde hier falsch gross.
        if (m) fontSize = parseFloat(m[1]!);
      }
      const c = mark.attrs?.['color'];
      if (typeof c === 'string' && c) color = c;
      // Keine Schriftart mehr: Seit @o2d/rich-text-editor 0.3.0 kennt das Schema
      // kein `fontFamily` auf `textStyle`, die Schrift kommt von aussen.
    } else if (mark.type === 'superscript' || mark.type === 'subscript') {
      // Klein gesetzt, aber ohne Grundlinien-Versatz — der Umbruch bleibt so
      // vorhersagbar. Bewusste Vereinfachung (mit Anke abgestimmt).
      fontSize = fontSize * 0.75;
    }
  }
  return { bold, italic, underline, strike, fontSize, color };
}

/** Sammelt die Absaetze eines Textdokuments samt Auszeichnung ein. */
export function collectBlocks(doc: TextDocument | undefined, obj: TextBox): TextBlock[] {
  const base = { fontSize: obj.fontSize, color: obj.color || '#000000' };
  const blocks: TextBlock[] = [];

  const walk = (node: TextDocumentNode, listMarker?: string): void => {
    const type = node.type;

    if (type === 'bulletList' || type === 'orderedList' || type === 'taskList') {
      (node.content ?? []).forEach((child, i) => {
        const marker =
          type === 'orderedList' ? `${i + 1}.` :
          type === 'taskList' ? (child.attrs?.['checked'] ? '☑' : '☐') :
          '•';
        walk(child, marker);
      });
      return;
    }
    if (type === 'listItem' || type === 'taskItem') {
      (node.content ?? []).forEach((child, i) => walk(child, i === 0 ? listMarker : undefined));
      return;
    }

    if (type === 'paragraph' || type === 'heading') {
      const runs: TextRun[] = [];
      const collect = (n: TextDocumentNode): void => {
        if (typeof n.text === 'string' && n.text) {
          runs.push({ text: n.text, ...markAttrs(n, base) });
        }
        (n.content ?? []).forEach(collect);
      };
      (node.content ?? []).forEach(collect);

      const rawAlign = node.attrs?.['textAlign'];
      const align = (typeof rawAlign === 'string' && rawAlign ? rawAlign : obj.hAlign) as TextBlock['align'];
      blocks.push({
        runs,
        align: align === 'center' || align === 'right' ? align : 'left',
        ...(listMarker ? { marker: listMarker } : {}),
      });
      return;
    }

    (node.content ?? []).forEach((child) => walk(child, listMarker));
  };

  (doc?.content ?? []).forEach((n) => walk(n));
  return blocks;
}

/** Eine fertig umbrochene Zeile. */
export interface LaidOutLine {
  readonly runs: readonly TextRun[];
  readonly width: number;
  readonly fontSize: number;
  readonly align: TextBlock['align'];
  readonly marker?: string;
}

/**
 * Bricht die Absaetze auf `maxWidth` um — gierig, Wort fuer Wort, wie es auch
 * der Browser tut. Laeufe werden dabei nicht zerschnitten: Eine Zeile kann
 * mehrere Laeufe enthalten, jeder behaelt seine Auszeichnung.
 */
export function layoutBlocks(
  blocks: readonly TextBlock[],
  maxWidth: number,
  measure: Measure,
): LaidOutLine[] {
  const lines: LaidOutLine[] = [];

  for (const block of blocks) {
    let current: TextRun[] = [];
    let width = 0;
    let firstLineOfBlock = true;

    const pushLine = (): void => {
      const fontSize = Math.max(...current.map((r) => r.fontSize), 1);
      lines.push({
        runs: current,
        width,
        fontSize,
        align: block.align,
        ...(firstLineOfBlock && block.marker ? { marker: block.marker } : {}),
      });
      firstLineOfBlock = false;
      current = [];
      width = 0;
    };

    for (const run of block.runs) {
      // Zeilenumbrueche im Text selbst respektieren.
      const chunks = run.text.split('\n');
      chunks.forEach((chunk, ci) => {
        if (ci > 0) pushLine();
        for (const word of chunk.split(/(\s+)/)) {
          if (!word) continue;
          const w = measure(word, run);
          if (width > 0 && width + w > maxWidth && word.trim()) {
            pushLine();
          }
          if (!word.trim() && width === 0) continue; // fuehrende Leerzeichen weg
          current.push({ ...run, text: word });
          width += w;
        }
      });
    }
    pushLine(); // auch leere Absaetze erzeugen eine Zeile (Leerzeile)
  }
  return lines;
}

function runStyle(run: TextRun): string {
  // Immer die portable Export-Schrift: Ein Lauf kann seit
  // @o2d/rich-text-editor 0.3.0 keine eigene Schriftart mehr tragen.
  let s = ` font-family="${esc(PORTABLE_FONT_FAMILY)}" font-size="${num(run.fontSize)}" fill="${esc(run.color)}"`;
  if (run.bold) s += ' font-weight="bold"';
  if (run.italic) s += ' font-style="italic"';
  const deco = [run.underline ? 'underline' : '', run.strike ? 'line-through' : ''].filter(Boolean).join(' ');
  if (deco) s += ` text-decoration="${deco}"`;
  return s;
}

/**
 * Der Textblock einer Textbox als `<text>`-Markup (lokale Koordinaten,
 * Ursprung = linke obere Ecke der Box).
 */
export function buildTextboxTextSvg(obj: TextBox, measure: Measure): string {
  const blocks = collectBlocks(obj.document, obj);
  if (blocks.length === 0) return '';

  const padV = obj.paddingV ?? 0;
  const padH = obj.paddingH ?? 0;
  const maxWidth = Math.max(1, obj.width - 2 * padH);
  const lines = layoutBlocks(blocks, maxWidth, measure);
  if (lines.length === 0) return '';

  const lineHeights = lines.map((l) => l.fontSize * EDIT_TEXTBOX_LINE_HEIGHT);
  const totalHeight = lineHeights.reduce((a, b) => a + b, 0);

  // Senkrechte Ausrichtung wie am Bildschirm.
  const free = Math.max(0, obj.height - 2 * padV - totalHeight);
  const startY = padV + (obj.vAlign === 'middle' ? free / 2 : obj.vAlign === 'bottom' ? free : 0);

  const parts: string[] = [];
  let y = startY;
  lines.forEach((line, i) => {
    const lh = lineHeights[i]!;
    const baseline = y + line.fontSize; // Grundlinie
    const anchor = line.align === 'center' ? 'middle' : line.align === 'right' ? 'end' : 'start';
    const x = line.align === 'center' ? obj.width / 2
      : line.align === 'right' ? obj.width - padH
      : padH;

    if (line.runs.length > 0 || line.marker) {
      const marker = line.marker ? `<tspan${runStyle(line.runs[0] ?? {
        text: '', bold: false, italic: false, underline: false, strike: false,
        fontSize: obj.fontSize, color: obj.color || '#000000',
      })}>${esc(line.marker)} </tspan>` : '';
      const spans = line.runs
        .map((r) => `<tspan${runStyle(r)}>${esc(r.text)}</tspan>`)
        .join('');
      parts.push(
        `<text x="${num(x)}" y="${num(baseline)}" text-anchor="${anchor}"` +
        ` xml:space="preserve">${marker}${spans}</text>`,
      );
    }
    y += lh;
  });

  return parts.join('');
}
